import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { createIcd10Diagnosis } from "../../../services/diagnosisService";
import { CLINICAL_GROUP_OPTIONS } from "../patientForm.shared";

const fieldClass =
  "block w-full rounded-md border border-slate-300 bg-white text-slate-900 text-sm py-1.5 px-2.5 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100";
const labelClass = "block text-[11px] font-medium text-slate-600 mb-0.5";
const ICD10_CODE_RE = /^[A-TV-Z][0-9][A-Z0-9.]{0,6}$/i;

/**
 * Modal to add a missing ICD-10 diagnosis into the shared catalog.
 * Required: code, description, clinical group.
 */
const AddDiagnosisModal = ({
  isOpen,
  onClose,
  onCreated,
  initialCode = "",
  initialDescription = "",
}) => {
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [clinicalGroup, setClinicalGroup] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const seed = String(initialCode || "").trim();
    const looksLikeCode = ICD10_CODE_RE.test(seed) && seed.length <= 8;
    setCode(looksLikeCode ? seed.toUpperCase() : "");
    setDescription(
      looksLikeCode ? "" : String(initialDescription || seed || "").trim(),
    );
    setClinicalGroup("");
    setErrors({});
    setSaving(false);
  }, [isOpen, initialCode, initialDescription]);

  if (!isOpen) return null;

  const validate = () => {
    const next = {};
    if (!String(code).trim()) next.code = "Code is required";
    else if (!ICD10_CODE_RE.test(String(code).trim())) {
      next.code = "Invalid ICD-10 code (e.g. E11.9)";
    }
    if (!String(description).trim()) next.description = "Description is required";
    if (!String(clinicalGroup).trim()) {
      next.clinicalGroup = "Clinical group is required";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || saving) return;
    setSaving(true);
    try {
      const res = await createIcd10Diagnosis({
        code: code.trim(),
        description: description.trim(),
        clinicalGroup,
      });
      const payload = res.result || res;
      const diagnosis = payload.diagnosis || payload;
      toast.success("Diagnosis added to catalog");
      onCreated?.(diagnosis);
      onClose?.();
    } catch (err) {
      const message =
        err?.message ||
        err?.error?.message ||
        "Failed to add diagnosis. Please try again.";
      toast.error(message);
      if (/already exists/i.test(message)) {
        setErrors((prev) => ({ ...prev, code: message }));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-diagnosis-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-rose-100 bg-rose-50/70">
          <h2
            id="add-diagnosis-title"
            className="text-sm font-semibold text-rose-900 uppercase tracking-wide"
          >
            Add Diagnosis
          </h2>
          <p className="mt-0.5 text-[11px] text-slate-600">
            Add a new ICD-10 code to the shared catalog for all users.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div className="space-y-1">
            <label className={labelClass}>
              Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={fieldClass}
              value={code}
              maxLength={16}
              placeholder="e.g. E11.9"
              autoFocus
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
            {errors.code ? (
              <p className="text-[11px] text-red-600">{errors.code}</p>
            ) : null}
          </div>

          <div className="space-y-1">
            <label className={labelClass}>
              Description <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={fieldClass}
              value={description}
              maxLength={500}
              placeholder="e.g. Type 2 diabetes mellitus without complications"
              onChange={(e) => setDescription(e.target.value)}
            />
            {errors.description ? (
              <p className="text-[11px] text-red-600">{errors.description}</p>
            ) : null}
          </div>

          <div className="space-y-1">
            <label className={labelClass}>
              Clinical Group <span className="text-red-500">*</span>
            </label>
            <select
              className={fieldClass}
              value={clinicalGroup}
              onChange={(e) => setClinicalGroup(e.target.value)}
            >
              <option value="">Select clinical group</option>
              {CLINICAL_GROUP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.clinicalGroup ? (
              <p className="text-[11px] text-red-600">{errors.clinicalGroup}</p>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              className="h-9 px-3 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="h-9 px-3 text-sm font-semibold rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {saving ? (
                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : null}
              Save Diagnosis
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDiagnosisModal;
